// SendHubPage — /send route: the "what do you want to send?" hub.
//
// Replaces the old top-level "Applications" nav entry. Staff land here and pick
// WHAT to send; each tile navigates to the surface that actually does it. The
// individual surfaces (the applicants list, renewal, test-lease, deposit, manual
// doc) keep their own routes — this is just the chooser.
//
// IT CAN NOW BE OPENED FOR SOMEBODY (sol finding 10). "Send…" on a household row
// links here with that household attached (`lib/sendContext`), so the hub says
// who it is for and every tile carries them onward — the payment-plan and
// deposit surfaces preselect the unit rather than starting from an empty picker,
// and the Lease tile goes straight to that household's builder instead of back
// to a list the operator just came from. Opened from the nav, with no context,
// this page is exactly what it was.

import { useNavigate, useSearchParams } from 'react-router-dom';
import {
  isEmptySendContext,
  sendContextFromSearch,
  withSendContext,
  type SendContext,
} from '@/lib/sendContext';

export interface SendTile {
  title: string;
  description: string;
  to: string;
  /**
   * Where this tile goes when the hub was opened FOR a household — when the
   * surface can do something better than "same page, plus a query string".
   * Returning null means the tile keeps its normal destination.
   */
  contextTo?: (ctx: SendContext) => string | null;
  /** A line added to the description when the hub has a household. */
  contextNote?: (ctx: SendContext) => string | null;
}

const TILES: SendTile[] = [
  {
    title: 'Lease',
    description: 'Build and send a new lease from the applicants list.',
    to: '/applications',
    // With a group in hand the applicants list is a detour — this household's
    // builder is the thing the operator was reaching for.
    contextTo: (ctx) =>
      ctx.groupId ? `/applications/${encodeURIComponent(ctx.groupId)}/lease` : null,
    contextNote: (ctx) => (ctx.groupId ? 'Opens this household’s lease builder.' : null),
  },
  {
    title: 'Deposit agreement',
    description: 'Send the move-out deposit-direction agreement to a past or on-notice tenancy.',
    to: '/send/deposit',
    contextNote: (ctx) => (ctx.unitId ? 'Their tenancy is highlighted in the list.' : null),
  },
  {
    title: 'Test lease',
    description: 'Generate a throwaway lease for layout and content checks.',
    to: '/test-lease',
  },
  {
    title: 'Renewal',
    description: "Send a renewal lease to a unit's existing tenants.",
    to: '/renewal',
  },
  {
    title: 'Payment plan',
    description: "Send a security-deposit payment-plan agreement to a unit's current residents.",
    to: '/send/payment-plan',
    contextNote: (ctx) =>
      ctx.propertyId && ctx.unitId ? 'Their property and unit are preselected.' : null,
  },
  {
    title: 'Adoption addendum',
    description:
      'Add residents to an existing lease when nobody is leaving — everyone already on it stays and signs.',
    to: '/send/adoption-addendum',
  },
  {
    title: 'Leaseholder change',
    description:
      'Send when an approved application joins an existing tenancy AND someone is moving out — added and released in one agreement.',
    to: '/send/leaseholder-change',
  },
  {
    title: 'Leaseholder removal',
    description:
      'Remove an outgoing resident from an existing lease; the remaining residents stay on.',
    to: '/send/leaseholder-removal',
  },
  {
    title: 'Animal addendum',
    description:
      'A current tenant is adding an animal: collect the animal’s details (and a photo) from the household, then send the Animal Addendum for signature.',
    // Deliberately NO contextTo/contextNote (Andrea, 2026-09-11): this is a Send-
    // page-only document. An applicant group's animals ride on the lease itself,
    // so opening it "for" a household would offer the wrong thing.
    to: '/send/animal-addendum',
  },
  {
    title: 'Manual doc',
    description: 'Upload your own PDF and send it to a tenancy for signing.',
    to: '/send/manual',
  },
  {
    title: 'Property management agreement',
    description: "Build and send an owner's Exclusive Property Management Agreement (all values entered by you).",
    to: '/send/property-management-agreement',
  },
  {
    title: 'W-9 request',
    description:
      'Email an owner or a vendor a secure link to fill in and sign their W-9; the taxpayer ID then goes onto their AppFolio record.',
    // No contextTo/contextNote: a W-9 is for an owner or a vendor, never a
    // household, so a household context has nothing to preselect here.
    to: '/send/w9-request',
  },
];

/** Where a tile goes, given the household (if any) the hub was opened for. */
export function tileDestination(tile: SendTile, ctx: SendContext): string {
  if (isEmptySendContext(ctx)) return tile.to;
  return tile.contextTo?.(ctx) ?? withSendContext(tile.to, ctx);
}

export function SendHubPage(): React.JSX.Element {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const ctx = sendContextFromSearch(searchParams);
  const forHousehold = !isEmptySendContext(ctx);

  return (
    <div style={{ maxWidth: 880 }}>
      <div style={{ marginBottom: 'var(--space-7)' }}>
        <h1 style={{ fontSize: 'var(--text-xl)', fontWeight: 'var(--weight-bold)', color: 'var(--fg)', margin: 0 }}>
          Send
        </h1>
        <p style={{ fontSize: 'var(--text-sm)', color: 'var(--fg-muted)', marginTop: 'var(--space-2)' }}>
          Pick what you want to send. Each option opens the surface that builds and dispatches it.
        </p>
      </div>

      {/* Who this is for. Names and an address — never an id, however many of
          them the URL is carrying to make the pickers work. */}
      {forHousehold && (
        <div
          className="alert"
          data-tone="info"
          role="note"
          style={{ marginBottom: 'var(--space-5)' }}
        >
          <div className="alert-body">
            <div className="alert-title">Sending for {ctx.name ?? 'this household'}</div>
            {ctx.where ? <div>{ctx.where}</div> : null}
            <div style={{ marginTop: 'var(--space-2)' }}>
              <button
                type="button"
                className="btn"
                data-variant="ghost"
                data-size="sm"
                onClick={() => navigate('/send')}
              >
                Send to somebody else
              </button>
            </div>
          </div>
        </div>
      )}

      <div className="grid-2">
        {TILES.map((tile) => {
          const to = tileDestination(tile, ctx);
          const note = forHousehold ? (tile.contextNote?.(ctx) ?? null) : null;
          return (
            <div
              key={tile.to}
              className="card"
              data-interactive="true"
              role="link"
              tabIndex={0}
              aria-label={tile.title}
              onClick={() => navigate(to)}
              onKeyDown={(e) => {
                if (e.key === 'Enter' || e.key === ' ') {
                  e.preventDefault();
                  navigate(to);
                }
              }}
            >
              <div className="stack-sm">
                <h3 className="card-title">{tile.title}</h3>
                <p style={{ fontSize: 'var(--text-sm)', color: 'var(--fg-muted)', margin: 0 }}>
                  {tile.description}
                </p>
                {note && (
                  <p style={{ fontSize: 'var(--text-xs)', color: 'var(--fg-muted)', margin: 0 }}>
                    {note}
                  </p>
                )}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

/** The tiles, exported so a test can assert every destination without a DOM. */
export const SEND_TILES: readonly SendTile[] = TILES;
